import { useQuery } from '@tanstack/react-query';
import apiClient from '../../API/apiClient';
import endpoints from '../../API/endpoints';
import {
  ACTIVE_VIDEO_NOTIFICATIONS_QUERY_KEY,
  VideoNotificationDto,
} from './constants';

/**
 * Agent-facing video-notification API — shared by B2C and Backoffice.
 * The operator CRUD endpoints live in the Backoffice module
 * (`modules/video-notification/VideoNotificationApi.tsx`).
 */

/**
 * GET /ms/video-notifications/active
 * Returns the pending queue for the logged-in agent. An empty array means
 * there is nothing to show. Anything the agent has already confirmed is
 * filtered out server-side, so every item here starts unseen.
 */
export const fetchActiveVideoNotifications = async (): Promise<
  VideoNotificationDto[]
> => {
  const res = await apiClient.get(endpoints.VIDEO_NOTIFICATIONS.ACTIVE);
  const videos = res?.data?.data?.videos;
  return Array.isArray(videos) ? videos : [];
};

/** POST /ms/video-notifications/:id/watched */
export const markVideoNotificationWatchedApi = (id: number | string) =>
  apiClient.post(
    `${endpoints.VIDEO_NOTIFICATIONS.MARK_WATCHED}/${id}/watched`
  );

interface UseActiveVideoNotificationsOptions {
  /**
   * Host permission gate — the request is not fired at all when false, so
   * users who cannot receive notifications never hit the endpoint.
   */
  enabled?: boolean;
}

export const useActiveVideoNotificationsApi = ({
  enabled = true,
}: UseActiveVideoNotificationsOptions = {}) => {
  const { data, isLoading, isFetching, refetch } = useQuery(
    [ACTIVE_VIDEO_NOTIFICATIONS_QUERY_KEY],
    fetchActiveVideoNotifications,
    {
      enabled,
      // the queue changes rarely; no need to refire on every window focus
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    notifications: data ?? [],
    isLoading,
    isFetching,
    refetch,
  };
};
